import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { ProjectDTO, ProjectResponse } from "@zplanem/responses"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { useJournalCacheInvalidator } from "../hooks/use-journal-cache-invalidator"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { ProposeCostModal } from "./propose-cost-modal"

export function ProjectCostSection({
	propertyId,
	project,
	canPropose,
	canDecide,
	canMarkPaid,
}: {
	propertyId: string
	project: ProjectDTO
	/** owner or architect∧member, project not paid */
	canPropose: boolean
	/** owner */
	canDecide: boolean
	/** owner or architect∧member, project not paid */
	canMarkPaid: boolean
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const queryClient = useQueryClient()
	const invalidateJournal = useJournalCacheInvalidator(propertyId)
	const [proposeOpen, setProposeOpen] = useState(false)
	const [costError, setCostError] = useState<string | null>(null)

	const base = `/api/common/properties/${propertyId}/projects/${project.id}`
	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: queryKeys.projects(propertyId) })
		invalidateJournal()
	}

	const decide = useMutation({
		mutationFn: ({
			proposalId,
			decision,
		}: {
			proposalId: string
			decision: "accept" | "reject"
		}) =>
			apiFetch<ProjectResponse>(
				`${base}/cost-proposals/${proposalId}/${decision}`,
				{ method: "POST", body: JSON.stringify({}) },
			),
		onSuccess: invalidate,
	})
	const markPaid = useMutation({
		mutationFn: () =>
			apiFetch<ProjectResponse>(`${base}/paid`, {
				method: "POST",
				body: JSON.stringify({}),
			}),
		onSuccess: invalidate,
	})

	async function run(action: Promise<unknown>) {
		setCostError(null)
		try {
			await action
		} catch (err) {
			setCostError(
				err instanceof ApiError ? err.message : t.projects.cost.errors.unknown,
			)
		}
	}

	const paid = project.paidAt !== null
	const accepted = project.acceptedCreationCost ?? null
	const pending = project.pendingCreationCost ?? null

	return (
		// No outer margin: the project Details tab renders this inside a card
		// whose padding owns the spacing (ZPL-126).
		<div>
			<div className="flex items-center justify-between">
				<h2 className="text-base font-bold text-foreground">
					{t.projects.cost.heading}
				</h2>
				{canPropose && !paid ? (
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setProposeOpen(true)}
					>
						{t.projects.cost.proposeCta}
					</Button>
				) : null}
			</div>

			{accepted ? (
				<div className="mt-3 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-success/20 bg-success/5">
					<div>
						<div className="text-xs text-muted-foreground">
							{t.projects.cost.acceptedLabel}
						</div>
						<div className="text-lg font-bold text-foreground">
							{accepted.amount} {accepted.currency}
						</div>
					</div>
					{paid ? (
						<span className="inline-flex items-center rounded-sm border border-current/25 px-2.5 py-0.5 text-xs font-medium bg-success-muted text-success-muted-foreground">
							{t.projects.cost.paidBadge}
						</span>
					) : canMarkPaid ? (
						<Button
							variant="brand"
							size="sm"
							onClick={() => run(markPaid.mutateAsync())}
							disabled={markPaid.isPending}
						>
							{t.projects.cost.markPaidCta}
						</Button>
					) : null}
				</div>
			) : null}

			{pending ? (
				<div className="mt-3 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border bg-muted/40">
					<div>
						<div className="text-xs text-muted-foreground">
							{t.projects.cost.pendingLabel}
						</div>
						<div className="text-base font-semibold text-foreground/80">
							{pending.amount} {pending.currency}
						</div>
					</div>
					{canDecide ? (
						<div className="flex items-center gap-2">
							<Button
								variant="brand"
								size="sm"
								onClick={() =>
									run(
										decide.mutateAsync({
											proposalId: pending.id,
											decision: "accept",
										}),
									)
								}
								disabled={decide.isPending}
							>
								{t.projects.cost.acceptCta}
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									run(
										decide.mutateAsync({
											proposalId: pending.id,
											decision: "reject",
										}),
									)
								}
								disabled={decide.isPending}
							>
								{t.projects.cost.rejectCta}
							</Button>
						</div>
					) : null}
				</div>
			) : null}

			{!accepted && !pending ? (
				<p className="mt-3 text-sm text-muted-foreground/80 italic">
					{t.projects.cost.noCost}
				</p>
			) : null}

			{!paid && accepted === null && canMarkPaid ? (
				<p className="mt-2 text-xs text-muted-foreground/80">
					{t.projects.cost.markPaidHint}
				</p>
			) : null}

			{costError ? (
				<div
					role="alert"
					className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
				>
					{costError}
				</div>
			) : null}

			{proposeOpen ? (
				<ProposeCostModal
					propertyId={propertyId}
					projectId={project.id}
					onClose={() => setProposeOpen(false)}
				/>
			) : null}
		</div>
	)
}
